import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Row, Col, Card, CardHeader, CardBody, Input, Button, Spinner, Collapse,
  FormGroup, Label, FormFeedback,
} from "reactstrap"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  adminGetPlans,
  adminCreatePlan,
  adminUpdatePlan,
  adminGetPlanFeatures,
  adminGetPlanIntegrations,
  adminGetCompanyPlanVisibility,
} from "../../api/requests/subscription-requests"
import { fetchCompaniesNames } from "../../api/requests/account-requests"
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D"

// Muhasebe entegrasyonu seçilince zorunlu olarak açılan özellik (PlanFeature adı).
const ACCOUNTING_MARKING_CODE = "ACCOUNTING_MARKING"

/**
 * Paket süresi seçenekleri.
 * Backend iki alan taşır: `durationDays` doluysa süre gün cinsindendir ve
 * `durationMonths` yok sayılır. 14 gün seçeneği bu yüzden days=14, months=0 gönderir.
 */
const DURATION_OPTIONS = [
  { key: "14D", label: "14 gün", months: 0, days: 14 },
  { key: "1M", label: "1 ay", months: 1, days: null },
  { key: "3M", label: "3 ay", months: 3, days: null },
  { key: "6M", label: "6 ay", months: 6, days: null },
  { key: "12M", label: "12 ay", months: 12, days: null },
]

const resolveDurationKey = (plan) => {
  if (!plan) return "12M"
  if (plan.durationDays) {
    const byDays = DURATION_OPTIONS.find((o) => o.days === plan.durationDays)
    return byDays ? byDays.key : "14D"
  }
  const byMonths = DURATION_OPTIONS.find((o) => o.days === null && o.months === plan.durationMonths)
  return byMonths ? byMonths.key : "12M"
}

const COLLECTION_PERIODS = [
  {
    value: "TOTAL",
    label: "Dönem toplamı",
    hint: "Limit paketin tüm süresi için geçerli. Örn: 12 ayda toplam 1.000.000 ₺.",
  },
  {
    value: "MONTHLY",
    label: "Her ay yeniden",
    hint: "Limit her takvim ayında sıfırlanır. Örn: Her ay ayrı 1.000.000 ₺.",
  },
]

const formatTRYAmount = (val) => {
  if (val === "" || val == null) return ""
  const num = typeof val === "number" ? val : parseFloat(val)
  if (isNaN(num)) return ""
  return num.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const parseTRYAmount = (raw) => {
  if (!raw) return ""
  const cleaned = String(raw).replace(/\./g, "").replace(",", ".")
  if (cleaned === "" || cleaned === ".") return ""
  const num = parseFloat(cleaned)
  return isNaN(num) ? "" : num
}

const sanitizeTRYInput = (raw) => {
  let s = String(raw || "").replace(/[^\d,.]/g, "").replace(/\./g, "")
  const firstComma = s.indexOf(",")
  if (firstComma !== -1) {
    s = s.slice(0, firstComma + 1) + s.slice(firstComma + 1).replace(/,/g, "")
    s = s.slice(0, firstComma + 3)
  }
  const [intPart, decPart] = s.split(",")
  const intFormatted = intPart ? Number(intPart).toLocaleString("tr-TR") : ""
  return decPart !== undefined ? `${intFormatted},${decPart}` : intFormatted
}

/**
 * `features` alanı üç ayrı biçimde saklanmış olabilir:
 *   1. JSON nesne  — {"items": ["50 cari hesap", ...]}   (V46 ile seed'lenen paketler)
 *   2. JSON dizi   — ["...", "..."]
 *   3. Düz metin   — satır satır
 * Form her zaman satır satır gösterir; kaydederken kaydın <b>kendi biçimi korunur</b>
 * ki mevcut paketlerin veri yapısı sessizce değişmesin.
 */
const detectFeaturesFormat = (raw) => {
  if (Array.isArray(raw)) return "array"
  if (typeof raw === "string") {
    const t = raw.trim()
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        const parsed = JSON.parse(t)
        if (Array.isArray(parsed)) return "array"
        if (parsed && Array.isArray(parsed.items)) return "items"
      } catch {
        // JSON değilse düz metin muamelesi görür
      }
    }
  }
  return "text"
}

/** Depolanan değeri, textarea'da gösterilecek satır listesine çevirir. */
const featuresToLines = (raw) => {
  if (!raw) return ""
  if (Array.isArray(raw)) return raw.join("\n")
  if (typeof raw !== "string") return ""

  const t = raw.trim()
  if (t.startsWith("{") || t.startsWith("[")) {
    try {
      const parsed = JSON.parse(t)
      if (Array.isArray(parsed)) return parsed.join("\n")
      if (parsed && Array.isArray(parsed.items)) return parsed.items.join("\n")
    } catch {
      // düşerse düz metin olarak kullan
    }
  }
  return raw
}

/** Textarea içeriğini, kaydın orijinal biçiminde geri yazar. */
const linesToFeatures = (text, format) => {
  const items = (text || "").split("\n").map((l) => l.trim()).filter(Boolean)
  if (items.length === 0) return null
  if (format === "items") return JSON.stringify({ items })
  if (format === "array") return JSON.stringify(items)
  return items.join("\n")
}

/** Numaralı başlıklı bölüm kartı — form uzun olduğu için görsel olarak parçalanır. */
const Section = ({ step, title, description, children }) => (
  <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
    <div className="d-flex align-items-start mb-3">
      <div
        className="d-flex align-items-center justify-content-center flex-shrink-0 me-3"
        style={{
          width: 32, height: 32, borderRadius: 999,
          background: NAVY, color: "#fff", fontWeight: 700, fontSize: ".85rem",
        }}
      >
        {step}
      </div>
      <div>
        <h6 className="mb-1 fw-semibold text-dark">{title}</h6>
        {description && (
          <div className="text-muted" style={{ fontSize: ".78rem" }}>{description}</div>
        )}
      </div>
    </div>
    {children}
  </div>
)

/**
 * Paket oluşturma / düzenleme sayfası.
 *
 * Modal yerine tam sayfa: form beş bölüme ayrıldı (kimlik, fiyat/süre, limitler,
 * özellikler, firmaya atama) ve her bölüm kendi kartında yaşıyor.
 */
const SubscriptionPlanForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  const [plan, setPlan] = useState(null)
  const [plans, setPlans] = useState([])
  const [featureCatalog, setFeatureCatalog] = useState([])
  // Entegrasyon kataloğu uçtan gelir; sabit liste tutulmaz ki yeni bir sağlayıcı
  // eklendiğinde bu ekran kendiliğinden güncellensin.
  const [integrationCatalog, setIntegrationCatalog] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [featuresOpen, setFeaturesOpen] = useState(false)
  // Düzenlenen paketin `features` biçimi — kaydederken aynısıyla geri yazılır.
  const [featuresFormat, setFeaturesFormat] = useState("text")
  const [basePriceDisplay, setBasePriceDisplay] = useState("")
  const [discountedPriceDisplay, setDiscountedPriceDisplay] = useState("")
  const [collectionAmountDisplay, setCollectionAmountDisplay] = useState("")

  document.title = `${isEdit ? "Paketi Düzenle" : "Yeni Paket"} | Tahsilat360`

  const styles = `
    .plan-form .form-check-input:checked { background-color: ${NAVY}; border-color: ${NAVY}; }
    .plan-form .feature-toggle {
      background: #fff; border: 1px solid #dfe3e9; border-radius: .6rem;
      padding: .65rem .9rem; text-align: left; transition: border-color .15s, box-shadow .15s;
    }
    .plan-form .feature-toggle:hover { border-color: ${NAVY}; }
    .plan-form .feature-toggle:focus { outline: none; box-shadow: 0 0 0 .18rem rgba(34,28,93,.15); }
    .plan-form .feature-list {
      border: 1px solid #eef1f5; border-radius: .6rem; padding: .35rem;
      max-height: 340px; overflow-y: auto; background: #fff;
      /* Liste sonuna gelindiğinde kaydırma sayfaya geçmesin (scroll chaining). */
      overscroll-behavior: contain;
    }
    .plan-form .feature-row {
      border-radius: .5rem; padding: .45rem .6rem; cursor: pointer;
      transition: background .15s;
    }
    .plan-form .feature-row:hover { background: #f6f8fb; }
    .plan-form .feature-row.is-selected { background: #f2f6fc; }
    .plan-form .feature-divider { border-top: 1px dashed #e3e8ef; margin: .35rem .3rem; }
    .plan-form .feature-cat {
      font-size: .66rem; text-transform: uppercase; letter-spacing: .04em;
      color: #97a1ae; white-space: nowrap; margin-left: .75rem;
    }
    .plan-form .feature-chip {
      display: inline-flex; align-items: center; gap: .25rem;
      background: #eef3fa; color: ${NAVY}; border: 1px solid #dbe4f0;
      border-radius: 999px; padding: .15rem .3rem .15rem .55rem;
      font-size: .72rem; font-weight: 600;
      cursor: pointer; transition: background .12s, border-color .12s, color .12s;
    }
    /* Rozete tiklamak ozelligi paketten cikarir; hover'da kaldirma rengine doner. */
    .plan-form .feature-chip:hover {
      background: #fdeeee; border-color: #f3c9c9; color: #c0392b;
    }
    .plan-form .feature-chip:focus-visible {
      outline: none; box-shadow: 0 0 0 .18rem rgba(192,57,43,.18);
    }
    .plan-form .feature-chip .chip-remove {
      display: inline-flex; font-size: .9rem; line-height: 1; opacity: .65;
    }
    .plan-form .feature-chip:hover .chip-remove { opacity: 1; }
    .plan-form .integration-picker {
      border: 1px solid #e3e8ef; border-radius: .6rem; padding: .75rem .85rem;
    }
    .plan-form .integration-group + .integration-group { margin-top: .75rem; }
    .plan-form .integration-cat {
      font-size: .66rem; text-transform: uppercase; letter-spacing: .04em;
      color: #97a1ae; font-weight: 700;
    }
    .plan-form .integration-all {
      font-size: .72rem; text-decoration: none; color: ${NAVY}; font-weight: 600;
    }
    .plan-form .integration-row {
      border: 1px solid #eef1f5; border-radius: .5rem; padding: .35rem .55rem;
      cursor: pointer; font-size: .82rem;
    }
    .plan-form .integration-row:hover { background: #f6f8fb; }
    .plan-form .integration-row.is-selected { background: #f2f6fc; border-color: #dbe4f0; }
    .plan-form .integration-row label { cursor: pointer; }
    .plan-form .assigned-list {
      max-height: 132px; overflow-y: auto; overscroll-behavior: contain;
      border: 1px solid #eef1f5; border-radius: .5rem; padding: .25rem;
    }
    .plan-form .assigned-row {
      font-size: .78rem; padding: .2rem .35rem; border-radius: .35rem;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
  `

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // Paket listesi hem düzenlenen paketi bulmak hem de "hangi paketler
        // görünsün" seçimi için gerekli; tek istekle ikisi de karşılanıyor.
        const [plansRes, featuresRes, integrationsRes] = await Promise.all([
          adminGetPlans(),
          adminGetPlanFeatures(),
          adminGetPlanIntegrations(),
        ])
        if (cancelled) return

        const list = plansRes.data || []
        setPlans(list)
        setFeatureCatalog(featuresRes.data || [])
        setIntegrationCatalog(integrationsRes.data || [])

        if (isEdit) {
          const found = list.find((p) => p.id === id)
          if (!found) {
            setError("Paket bulunamadı.")
          } else {
            setPlan(found)
            setFeaturesFormat(detectFeaturesFormat(found.features))
            setBasePriceDisplay(found.basePrice != null ? formatTRYAmount(found.basePrice) : "")
            setDiscountedPriceDisplay(
              found.discountedPrice != null ? formatTRYAmount(found.discountedPrice) : ""
            )
            setCollectionAmountDisplay(
              found.maxCollectionAmount != null ? formatTRYAmount(found.maxCollectionAmount) : ""
            )
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }

      try {
        const res = await fetchCompaniesNames()
        if (!cancelled) setCompanies(res?.data || [])
      } catch {
        // Firma listesi alınamazsa atama bölümü devre dışı kalır.
      }
    })()
    return () => { cancelled = true }
  }, [id, isEdit])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: plan?.name || "",
      description: plan?.description || "",
      basePrice: plan?.basePrice ?? "",
      discountedPrice: plan?.discountedPrice ?? "",
      vatRate: plan?.vatRate ?? 20,
      vatIncluded: plan?.vatIncluded ?? true,
      durationKey: resolveDurationKey(plan),
      sortOrder: plan?.sortOrder ?? 0,
      active: plan?.active ?? true,

      accountLimitEnabled: !!(plan?.maxAccounts || plan?.maxUsers || plan?.maxAccountUsers),
      maxAccounts: plan?.maxAccounts ?? "",
      maxUsers: plan?.maxUsers ?? "",
      maxAccountUsers: plan?.maxAccountUsers ?? "",
      maxAccountGroups: plan?.maxAccountGroups ?? "",

      collectionLimitEnabled: !!(plan?.maxCollectionAmount || plan?.maxCollectionCount),
      maxCollectionAmount: plan?.maxCollectionAmount ?? "",
      maxCollectionCount: plan?.maxCollectionCount ?? "",
      collectionLimitPeriod: plan?.collectionLimitPeriod || "TOTAL",

      planFeatures: plan?.planFeatures || [],
      planIntegrations: plan?.planIntegrations || [],
      features: featuresToLines(plan?.features),

      trialAllowed: plan?.trialAllowed ?? false,

      assignToCompanyId: "",
      offerOtherPlans: true,
      visiblePlanIds: [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Paket adı gerekli"),
      basePrice: Yup.number().min(0, "Fiyat negatif olamaz").required("Fiyat gerekli"),
      // İndirim liste fiyatının ALTINDA olmalı; üstünde bir değer fiyatı yükseltmez,
      // backend onu sessizce yok sayar. Kullanıcı bunu kaydetmeden görsün.
      discountedPrice: Yup.number()
        .transform((v, o) => (o === "" ? undefined : v))
        .min(0, "İndirimli fiyat negatif olamaz")
        .test(
          "below-base",
          "İndirimli fiyat, liste fiyatının altında olmalı",
          function (value) {
            if (value == null || value === "") return true
            const base = Number(this.parent.basePrice)
            if (!Number.isFinite(base)) return true
            return Number(value) < base
          }
        ),
      vatRate: Yup.number().min(0).max(100).required("KDV oranı gerekli"),
      maxCollectionAmount: Yup.number()
        .transform((v, o) => (o === "" ? undefined : v))
        .positive("Tutar limiti 0'dan büyük olmalı"),
      maxCollectionCount: Yup.number()
        .transform((v, o) => (o === "" ? undefined : v))
        .positive("Adet limiti 0'dan büyük olmalı")
        .integer("Adet tam sayı olmalı"),
    }),
    onSubmit: async (values) => {
      setSaving(true)
      try {
        const duration = DURATION_OPTIONS.find((o) => o.key === values.durationKey) || DURATION_OPTIONS[4]
        const limitOn = values.collectionLimitEnabled

        const payload = {
          name: values.name,
          description: values.description,
          durationMonths: duration.months,
          durationDays: duration.days,
          basePrice: values.basePrice,
          discountedPrice: values.discountedPrice === "" ? null : values.discountedPrice,
          vatRate: values.vatRate,
          vatIncluded: values.vatIncluded,
          maxAccounts: values.accountLimitEnabled ? (values.maxAccounts || null) : null,
          maxUsers: values.accountLimitEnabled ? (values.maxUsers || null) : null,
          maxAccountUsers: values.accountLimitEnabled ? (values.maxAccountUsers || null) : null,
          maxAccountGroups: values.maxAccountGroups === "" ? null : parseInt(values.maxAccountGroups, 10),
          features: linesToFeatures(values.features, featuresFormat),
          sortOrder: values.sortOrder,
          active: values.active,
          trialAllowed: values.trialAllowed,

          maxCollectionAmount: limitOn && values.maxCollectionAmount !== "" ? values.maxCollectionAmount : null,
          maxCollectionCount: limitOn && values.maxCollectionCount !== "" ? parseInt(values.maxCollectionCount, 10) : null,
          collectionLimitPeriod: values.collectionLimitPeriod,

          planFeatures: values.planFeatures,
          planIntegrations: values.planIntegrations,
        }

        if (values.assignToCompanyId) {
          payload.assignToCompanyId = values.assignToCompanyId
          payload.offerOtherPlans = values.offerOtherPlans
          payload.visiblePlanIds = values.offerOtherPlans ? values.visiblePlanIds : []
        }

        const res = isEdit
          ? await adminUpdatePlan(id, payload)
          : await adminCreatePlan(payload)

        toast.success(res?.message || (isEdit ? "Paket güncellendi" : "Paket oluşturuldu"))
        navigate("/admin/subscription-plans")
      } catch (err) {
        toast.error(err.message)
      } finally {
        setSaving(false)
      }
    },
  })

  // İndirim önizlemesi — yönetici kaydetmeden müşterinin göreceği fiyatı görsün.
  // Backend ile aynı kural: indirim ancak liste fiyatının altındaysa geçerli.
  const discountPreview = useMemo(() => {
    const base = Number(formik.values.basePrice)
    const discounted = Number(formik.values.discountedPrice)
    if (!Number.isFinite(base) || base <= 0) return null
    if (formik.values.discountedPrice === "" || !Number.isFinite(discounted)) return null
    if (discounted < 0 || discounted >= base) return null
    return {
      base: formatTRYAmount(base),
      discounted: formatTRYAmount(discounted),
      percentage: Math.round(((base - discounted) / base) * 100),
    }
  }, [formik.values.basePrice, formik.values.discountedPrice])

  // Firma seçilince o firmanın mevcut ayarı forma çekilir; yönetici hâlihazırdaki
  // durumu görsün ve yanlışlıkla sıfırlamasın.
  const selectedCompanyId = formik.values.assignToCompanyId
  useEffect(() => {
    if (!selectedCompanyId) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await adminGetCompanyPlanVisibility(selectedCompanyId)
        if (cancelled || !res?.data) return
        formik.setFieldValue("offerOtherPlans", res.data.offerOtherPlans ?? true)
        formik.setFieldValue("visiblePlanIds", res.data.visiblePlanIds || [])
      } catch {
        // Okunamazsa formdaki varsayılanlar geçerli kalır.
      }
    })()
    return () => { cancelled = true }
  }, [selectedCompanyId]) // eslint-disable-line

  const toggleIntegration = (code) => {
    const current = formik.values.planIntegrations || []
    formik.setFieldValue(
      "planIntegrations",
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    )
  }

  /** Bir kategorinin (Ödeme Kuruluşu / Banka / Muhasebe) tamamını seç ya da tümünü kaldır. */
  const toggleIntegrationCategory = (category) => {
    const current = formik.values.planIntegrations || []
    const codes = integrationCatalog.filter((i) => i.category === category).map((i) => i.code)
    const allSelected = codes.every((c) => current.includes(c))
    formik.setFieldValue(
      "planIntegrations",
      allSelected ? current.filter((c) => !codes.includes(c)) : [...new Set([...current, ...codes])]
    )
  }

  /**
   * Muhasebe entegrasyonu seçiliyse muhasebeleştirme özelliği de açıktır.
   * Kural backend'de de var (PlanFeatureGuard); burada ayrıca uygulanmasının
   * sebebi yöneticinin ikisini çelişik bırakabilmesiydi — entegrasyonu satıp
   * tahsilatı muhasebeleşti olarak işaretlemeyi kapatmak anlamsız bir kombinasyon.
   */
  const accountingIntegrationSelected = useMemo(() => {
    const selected = formik.values.planIntegrations || []
    return integrationCatalog.some((i) => i.accounting && selected.includes(i.code))
  }, [integrationCatalog, formik.values.planIntegrations])

  const isImpliedFeature = (code) =>
    code === ACCOUNTING_MARKING_CODE && accountingIntegrationSelected

  // Seçim kayda da yazılır: müşteriye gösterilen paket kartı doğrudan bu listeyi
  // okuyor, yalnız çalışma anında türetmek kartı eksik bırakırdı.
  useEffect(() => {
    const current = formik.values.planFeatures || []
    if (!accountingIntegrationSelected || current.includes(ACCOUNTING_MARKING_CODE)) return
    formik.setFieldValue("planFeatures", [...current, ACCOUNTING_MARKING_CODE])
  }, [accountingIntegrationSelected, formik.values.planFeatures]) // eslint-disable-line

  const toggleFeature = (code) => {
    // Entegrasyonun zorunlu kıldığı özellik elle kapatılamaz; backend zaten açık
    // sayardı, form onu kapalı gösterseydi iki taraf çelişirdi.
    if (isImpliedFeature(code)) return
    const current = formik.values.planFeatures || []
    formik.setFieldValue(
      "planFeatures",
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    )
  }

  const toggleVisiblePlan = (planId) => {
    const current = formik.values.visiblePlanIds || []
    formik.setFieldValue(
      "visiblePlanIds",
      current.includes(planId) ? current.filter((x) => x !== planId) : [...current, planId]
    )
  }

  const selectedFeatures = formik.values.planFeatures || []
  const selectedFeatureCount = selectedFeatures.length

  /**
   * Katalog, seçililer üstte olacak şekilde sıralanır. Her grup kendi içinde
   * katalog sırasını (kategori bazlı doğal sıra) korur; böylece bir özelliği
   * seçmek listeyi tümden karıştırmaz, sadece o satırı yukarı taşır.
   */
  const selectedIntegrations = formik.values.planIntegrations || []

  /** Katalog kategoriye göre gruplanır; sıra backend'den geldiği gibi korunur. */
  const integrationGroups = useMemo(() => {
    const groups = []
    integrationCatalog.forEach((i) => {
      let g = groups.find((x) => x.category === i.category)
      if (!g) { g = { category: i.category, items: [] }; groups.push(g) }
      g.items.push(i)
    })
    return groups
  }, [integrationCatalog])

  const sortedFeatures = useMemo(() => {
    const sel = [], rest = []
    featureCatalog.forEach((f) => (selectedFeatures.includes(f.code) ? sel : rest).push(f))
    return [...sel, ...rest]
  }, [featureCatalog, selectedFeatures])

  // Bu paketi fiilen kullanan firmalar (aktif/deneme abonelikler). Backend paket
  // listesiyle birlikte döndürür; ayrı istek gerekmez.
  const assignedCompanies = plan?.assignedCompanyNames || []

  // Arama kutusu için {value,label} listesi. Firma adı iki alandan biriyle gelebiliyor.
  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.companyName || c.name })),
    [companies]
  )

  // "Herkese açık" paketler = firmaya sunulabilecek adaylar. Deneme paketleri ve
  // firmaya özel (gizli) paketler bu listede yer almaz; düzenlenen paketin kendisi de.
  const publicPlans = useMemo(
    () => plans.filter((p) => !p.trial && p.visibleToCustomers !== false && p.id !== id),
    [plans, id]
  )

  const selectedCompanyName = companies.find((c) => c.id === selectedCompanyId)?.companyName || "Seçilen firma"

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5">
          <Spinner style={{ color: NAVY }} />
        </div>
      </div>
    )
  }

  return (
    <div className="page-content page-common plan-form">
      <style>{styles}</style>
      <div className="container-fluid">

        {/* Başlık */}
        <div className="page-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2">
            <BackButton to={"/admin/subscription-plans"} />
            <div>
              <h5 className="mb-0 fw-semibold text-dark">
                {isEdit ? "Paketi Düzenle" : "Yeni Paket Oluştur"}
              </h5>
              <div className="text-muted" style={{ fontSize: ".78rem" }}>
                {isEdit ? plan?.name : "Paket bilgilerini, limitleri ve özellikleri tanımlayın"}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="border border-danger rounded-3 px-3 py-2 mb-3 text-danger" style={{ background: "#fdf2f2" }}>
            <i className="bx bx-error-circle me-1" />
            {error}
          </div>
        )}

        <Row>
          <Col lg={12}>
            {/* Tek beyaz kart, icinde gri bolum kutulari — Kullanici Duzenleme
                ekraniyla ayni desen. Aksiyonlar da beyazin icinde, sag altta. */}
            <Card className="detail-card">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                    <i className="bx bx-package" />
                  </div>
                  Paket Ayarları
                </div>
              </CardHeader>
              <CardBody className="p-4">

                {/* 1 — Paket bilgileri */}
                <Section step="1" title="Paket Bilgileri" description="Panelde ve müşteri paket kartında görünen temel bilgiler.">
                  <Row>
                    <Col md={8}>
                      <FormGroup>
                        <Label>Paket Adı</Label>
                        <Input
                          name="name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.name && !!formik.errors.name}
                          placeholder="Örn. Kurumsal"
                        />
                        <FormFeedback>{formik.errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Sıralama</Label>
                        <Input type="number" name="sortOrder" value={formik.values.sortOrder} onChange={formik.handleChange} />
                        <div className="text-muted" style={{ fontSize: ".72rem" }}>Küçük değer önce listelenir.</div>
                      </FormGroup>
                    </Col>
                  </Row>
                  <FormGroup className="mb-0">
                    <Label>Açıklama</Label>
                    <Input type="textarea" name="description" rows={2} value={formik.values.description} onChange={formik.handleChange} />
                  </FormGroup>
                </Section>

                {/* 2 — Fiyat ve süre */}
                <Section step="2" title="Fiyat ve Süre" description="Paketin ücreti, KDV durumu ve geçerlilik süresi.">
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Liste Fiyatı (₺)</Label>
                        <Input
                          name="basePrice"
                          value={basePriceDisplay}
                          onChange={(e) => {
                            const formatted = sanitizeTRYInput(e.target.value)
                            setBasePriceDisplay(formatted)
                            formik.setFieldValue("basePrice", parseTRYAmount(formatted))
                          }}
                          invalid={formik.touched.basePrice && !!formik.errors.basePrice}
                          placeholder="0"
                        />
                        <FormFeedback>{formik.errors.basePrice}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>İndirimli Fiyat (₺)</Label>
                        <Input
                          name="discountedPrice"
                          value={discountedPriceDisplay}
                          onChange={(e) => {
                            const formatted = sanitizeTRYInput(e.target.value)
                            setDiscountedPriceDisplay(formatted)
                            formik.setFieldValue(
                              "discountedPrice",
                              formatted === "" ? "" : parseTRYAmount(formatted)
                            )
                          }}
                          invalid={formik.touched.discountedPrice && !!formik.errors.discountedPrice}
                          placeholder="İndirim yok"
                        />
                        <FormFeedback>{formik.errors.discountedPrice}</FormFeedback>
                        <div className="text-muted" style={{ fontSize: ".72rem" }}>
                          {discountPreview
                            ? `Müşteri ${discountPreview.discounted} ₺ öder; ${discountPreview.base} ₺ üzeri çizili görünür.`
                            : "Boş bırakılırsa indirim uygulanmaz."}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>KDV Oranı (%)</Label>
                        <Input type="number" name="vatRate" value={formik.values.vatRate} onChange={formik.handleChange} />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Paket Süresi</Label>
                        {plan?.unlimitedDuration ? (
                          <Input type="text" value="Süresiz" disabled />
                        ) : (
                          <Input type="select" name="durationKey" value={formik.values.durationKey} onChange={formik.handleChange}>
                            {DURATION_OPTIONS.map((o) => (
                              <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                          </Input>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="vatIncluded"
                      name="vatIncluded"
                      checked={formik.values.vatIncluded}
                      onChange={formik.handleChange}
                    />
                    <label className="form-check-label" htmlFor="vatIncluded">Fiyata KDV dahil</label>
                  </div>
                </Section>

                {/* 3 — Limitler */}
                <Section step="3" title="Limitler" description="Açılmayan limit sınırsız kabul edilir.">
                  {/* Cari / kullanıcı limiti — tahsilat limitiyle aynı desen: önce aç, sonra sayı gir */}
                  <div className="border rounded-3 p-3 mb-3" style={{ background: "#fbfcfe" }}>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="accountLimitEnabled"
                        name="accountLimitEnabled"
                        checked={formik.values.accountLimitEnabled}
                        onChange={formik.handleChange}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="accountLimitEnabled">
                        Cari ve kullanıcı limiti olsun
                      </label>
                      <div className="text-muted" style={{ fontSize: ".75rem" }}>
                        Kapalıysa firma sınırsız cari ve kullanıcı oluşturabilir. Açıkken boş
                        bıraktığınız kutu tek başına sınırsız sayılır.
                      </div>
                    </div>

                    {formik.values.accountLimitEnabled && (
                      <Row className="mt-3">
                        <Col md={6}>
                          <FormGroup className="mb-3 mb-md-0">
                            <Label>Maks. Cari Sayısı</Label>
                            <Input type="number" name="maxAccounts" min={1} placeholder="Sınırsız" value={formik.values.maxAccounts} onChange={formik.handleChange} />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-3 mb-md-0">
                            <Label>Maks. Alt Kullanıcı Sayısı</Label>
                            <Input type="number" name="maxUsers" min={1} placeholder="Sınırsız" value={formik.values.maxUsers} onChange={formik.handleChange} />
                            <small className="text-muted">Firmanın kendi personeli. Cari kullanıcıları buraya dahil değildir.</small>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    {formik.values.accountLimitEnabled && (
                      <Row className="mt-3">
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label>Maks. Cari Kullanıcı Sayısı</Label>
                            <Input type="number" name="maxAccountUsers" min={1} placeholder="Sınırsız" value={formik.values.maxAccountUsers} onChange={formik.handleChange} />
                            <small className="text-muted">Carilere tanımlanan kullanıcılar. Alt kullanıcı hakkından düşmez.</small>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    {/* Taksit grubu her firmada tanımlanabilir olmalı — grup olmadan cari
                        kullanıcısı ödeme yapamıyor. Paket yalnız adedini sınırlar. */}
                    <Row className="mt-3">
                      <Col md={6}>
                        <FormGroup className="mb-0">
                          <Label>Maks. Taksit Grubu</Label>
                          <Input type="number" name="maxAccountGroups" min={1} placeholder="Sınırsız"
                                 value={formik.values.maxAccountGroups} onChange={formik.handleChange} />
                          <div className="text-muted" style={{ fontSize: ".72rem" }}>
                            Boş bırakılırsa sınırsız.
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <div className="border rounded-3 p-3" style={{ background: "#fbfcfe" }}>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="collectionLimitEnabled"
                        name="collectionLimitEnabled"
                        checked={formik.values.collectionLimitEnabled}
                        onChange={formik.handleChange}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="collectionLimitEnabled">
                        Tahsilat limiti olsun
                      </label>
                      <div className="text-muted" style={{ fontSize: ".75rem" }}>
                        Limit dolduğunda tahsilat engellenir. Kart ve elle girilen tahsilatların
                        tamamı sayılır; iade edilen tutarlar düşülür.
                      </div>
                    </div>

                    {formik.values.collectionLimitEnabled && (
                      <>
                        <Row className="mt-3">
                          <Col md={6}>
                            <FormGroup>
                              <Label>Tutar Limiti (₺)</Label>
                              <Input
                                value={collectionAmountDisplay}
                                onChange={(e) => {
                                  const formatted = sanitizeTRYInput(e.target.value)
                                  setCollectionAmountDisplay(formatted)
                                  formik.setFieldValue("maxCollectionAmount", parseTRYAmount(formatted))
                                }}
                                placeholder="Sınırsız"
                                invalid={!!formik.errors.maxCollectionAmount}
                              />
                              <FormFeedback>{formik.errors.maxCollectionAmount}</FormFeedback>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>İşlem Adedi Limiti</Label>
                              <Input
                                type="number"
                                name="maxCollectionCount"
                                min={1}
                                placeholder="Sınırsız"
                                value={formik.values.maxCollectionCount}
                                onChange={formik.handleChange}
                                invalid={!!formik.errors.maxCollectionCount}
                              />
                              <FormFeedback>{formik.errors.maxCollectionCount}</FormFeedback>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Label className="mb-1">Limit Periyodu</Label>
                        <Row>
                          {COLLECTION_PERIODS.map((p) => {
                            const selected = formik.values.collectionLimitPeriod === p.value
                            return (
                              <Col md={6} key={p.value} className="mb-2">
                                <div
                                  className="border rounded-3 p-2 h-100"
                                  style={{
                                    cursor: "pointer",
                                    borderColor: selected ? NAVY : undefined,
                                    background: selected ? "#f2f6fc" : "#fff",
                                  }}
                                  onClick={() => formik.setFieldValue("collectionLimitPeriod", p.value)}
                                >
                                  <div className="form-check mb-1">
                                    <input
                                      type="radio"
                                      className="form-check-input"
                                      id={`period-${p.value}`}
                                      name="collectionLimitPeriod"
                                      value={p.value}
                                      checked={selected}
                                      onChange={formik.handleChange}
                                    />
                                    <label className="form-check-label fw-semibold" htmlFor={`period-${p.value}`}>
                                      {p.label}
                                    </label>
                                  </div>
                                  <div className="text-muted" style={{ fontSize: ".72rem" }}>{p.hint}</div>
                                </div>
                              </Col>
                            )
                          })}
                        </Row>
                      </>
                    )}
                  </div>
                </Section>

                {/* 4 — Özellikler */}
                <Section
                  step="4"
                  title="Paket Özellikleri"
                  description="Seçilmeyen özellikler bu paketteki firmalara kapatılır: menü gizlenir, API isteği reddedilir."
                >
                  {featureCatalog.length === 0 ? (
                    <div className="text-muted small">Özellik kataloğu yüklenemedi.</div>
                  ) : (
                    <div className="feature-picker">
                      {/* Açılır liste başlığı — kaç özellik seçildiğini özetler */}
                      <button
                        type="button"
                        className="feature-toggle w-100 d-flex align-items-center justify-content-between"
                        onClick={() => setFeaturesOpen((o) => !o)}
                        aria-expanded={featuresOpen}
                      >
                        <span className="d-flex align-items-center gap-2">
                          <i className="bx bx-list-check" style={{ color: NAVY }} />
                          <span className="fw-semibold">
                            {selectedFeatureCount > 0
                              ? `${selectedFeatureCount} özellik seçildi`
                              : "Özellik seçilmedi"}
                          </span>
                          <span className="text-muted" style={{ fontSize: ".75rem" }}>
                            / {featureCatalog.length}
                          </span>
                        </span>
                        <i className={`bx ${featuresOpen ? "bx-chevron-up" : "bx-chevron-down"}`} style={{ fontSize: "1.15rem" }} />
                      </button>

                      {/* Seçili özellikler kapalıyken de görünsün — özet rozetleri */}
                      {!featuresOpen && selectedFeatureCount > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {sortedFeatures
                            .filter((f) => selectedFeatures.includes(f.code))
                            .map((f) => (
                              <button
                                key={f.code}
                                type="button"
                                className="feature-chip"
                                onClick={() => toggleFeature(f.code)}
                                title={`"${f.label}" özelliğini paketten çıkar`}
                                aria-label={`${f.label} özelliğini paketten çıkar`}
                              >
                                {f.label}
                                <i className="bx bx-x chip-remove" aria-hidden="true" />
                              </button>
                            ))}
                        </div>
                      )}

                      <Collapse isOpen={featuresOpen}>
                        <div className="feature-list mt-2">
                          {sortedFeatures.map((f, i) => {
                            const implied = isImpliedFeature(f.code)
                            const checked = selectedFeatures.includes(f.code) || implied
                            // Seçililerle seçilmeyenler arasına tek bir ayraç konur.
                            const isFirstUnselected = !checked && i > 0 && selectedFeatures.includes(sortedFeatures[i - 1].code)
                            return (
                              <React.Fragment key={f.code}>
                                {isFirstUnselected && <div className="feature-divider" />}
                                <div
                                  className={`feature-row d-flex align-items-center justify-content-between ${checked ? "is-selected" : ""}`}
                                  onClick={() => toggleFeature(f.code)}
                                >
                                  <div className="form-check mb-0 d-flex align-items-center gap-2">
                                    <input
                                      type="checkbox"
                                      className="form-check-input mt-0"
                                      id={`feat-${f.code}`}
                                      checked={checked}
                                      disabled={implied}
                                      onChange={() => toggleFeature(f.code)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <label className="form-check-label mb-0" htmlFor={`feat-${f.code}`} onClick={(e) => e.stopPropagation()}>
                                      {f.label}
                                      {implied && (
                                        <span
                                          className="badge bg-light text-muted border ms-1"
                                          style={{ fontSize: ".6rem" }}
                                          title="Pakette muhasebe entegrasyonu seçili olduğu için bu özellik zorunlu olarak açık"
                                        >
                                          entegrasyon gereği
                                        </span>
                                      )}
                                      {/* Sınırsız Tahsilat'ın karşılığı yukarıdaki tahsilat limiti alanı. */}
                                      {!f.enforced && f.code !== "CREDIT_CARD_COLLECTION" && (
                                        <span
                                          className="badge bg-light text-muted border ms-1"
                                          style={{ fontSize: ".6rem" }}
                                          title="Teknik olarak kısıtlanamaz; yalnızca paket kartında görünür"
                                        >
                                          bilgi
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                  <span className="feature-cat">{f.category}</span>
                                </div>
                              </React.Fragment>
                            )
                          })}
                        </div>
                      </Collapse>
                    </div>
                  )}

                  {/* Entegrasyonlar — özellik seçicinin hemen altında.
                      Katalog uçtan gelir; yeni bir sağlayıcı eklendiğinde burası
                      kendiliğinden güncellenir, listede sabit isim tutulmaz. */}
                  <div className="integration-picker mt-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                      <span className="d-flex align-items-center gap-2">
                        <i className="bx bx-plug" style={{ color: NAVY }} />
                        <span className="fw-semibold">Kullanılabilir Entegrasyonlar</span>
                      </span>
                      <span className="text-muted" style={{ fontSize: ".75rem" }}>
                        {selectedIntegrations.length} / {integrationCatalog.length} seçili
                      </span>
                    </div>
                    <div className="text-muted mb-2" style={{ fontSize: ".72rem" }}>
                      Hiçbiri seçilmezse kısıtlama uygulanmaz; paket tüm entegrasyonlara açıktır.
                    </div>

                    {integrationCatalog.length === 0 ? (
                      <div className="text-muted small">Entegrasyon kataloğu yüklenemedi.</div>
                    ) : (
                      integrationGroups.map((g) => {
                        const codes = g.items.map((i) => i.code)
                        const allSelected = codes.every((c) => selectedIntegrations.includes(c))
                        return (
                          <div key={g.category} className="integration-group">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <span className="integration-cat">{g.category}</span>
                              <button
                                type="button"
                                className="btn btn-link p-0 integration-all"
                                onClick={() => toggleIntegrationCategory(g.category)}
                              >
                                {allSelected ? "Tümünü kaldır" : "Tümünü seç"}
                              </button>
                            </div>
                            <Row className="g-2">
                              {g.items.map((i) => {
                                const checked = selectedIntegrations.includes(i.code)
                                return (
                                  <Col xs={6} md={4} key={i.code}>
                                    <div
                                      className={`integration-row d-flex align-items-center gap-2 ${checked ? "is-selected" : ""}`}
                                      onClick={() => toggleIntegration(i.code)}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-0"
                                        id={`intg-${i.code}`}
                                        checked={checked}
                                        onChange={() => toggleIntegration(i.code)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <label
                                        className="form-check-label mb-0"
                                        htmlFor={`intg-${i.code}`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {i.label}
                                      </label>
                                    </div>
                                  </Col>
                                )
                              })}
                            </Row>
                          </div>
                        )
                      })
                    )}
                  </div>

                  <FormGroup className="mt-2 mb-0">
                    <Label>Ek Özellikler (opsiyonel)</Label>
                    <Input
                      type="textarea"
                      name="features"
                      rows={3}
                      placeholder={"Sınırsız ödeme alma\nCari yönetimi\nRaporlama"}
                      value={formik.values.features}
                      onChange={formik.handleChange}
                    />
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>
                      Her satıra bir özellik.
                    </div>
                  </FormGroup>
                </Section>

                {/* 5 — Firmaya atama */}
                <Section
                  step="5"
                  title="Firmaya Ata (opsiyonel)"
                  description="Paket kaydedildikten hemen sonra seçilen firmaya atanır ve firmanın mevcut aboneliği bununla değiştirilir."
                >
                  <FormGroup>
                    <Label>Firma</Label>
                    {/* Firma sayısı arttıkça açılır liste kullanışsızlaşıyor; adı yazarak
                        arayıp seçmek için react-select (projedeki diğer firma seçicilerle aynı). */}
                    <Select
                      options={companyOptions}
                      value={companyOptions.find((o) => o.value === formik.values.assignToCompanyId) || null}
                      onChange={(opt) => formik.setFieldValue("assignToCompanyId", opt?.value ?? "")}
                      placeholder="Firma adı yazın veya seçin..."
                      isClearable
                      isDisabled={companies.length === 0}
                      noOptionsMessage={() => "Firma bulunamadı"}
                      loadingMessage={() => "Yükleniyor..."}
                      // Aynı anda 3 firma görünür, gerisi kaydırılır (satır ~38px).
                      maxMenuHeight={114}
                      styles={{
                        menu: (base) => ({ ...base, zIndex: 20 }),
                        // İç liste sonuna gelince kaydırma sayfaya geçmesin.
                        menuList: (base) => ({ ...base, overscrollBehavior: "contain" }),
                      }}
                    />
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>
                      {companies.length === 0
                        ? "Firma listesi yüklenemedi."
                        : "Boş bırakılırsa atama yapılmaz."}
                    </div>
                  </FormGroup>

                  {selectedCompanyId && (
                    <>
                      <div
                        className="border rounded-3 px-3 py-2 mb-3 text-muted"
                        style={{ fontSize: ".78rem", background: "#f8fafc" }}
                      >
                        <i className="bx bx-info-circle me-1" />
                        Bu paket <b>{selectedCompanyName}</b> için oluşturulduğundan herkese açık
                        paket listesinde görünmez; yalnızca atadığınız firmalarda geçerli olur.
                      </div>

                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="offerOtherPlans"
                          name="offerOtherPlans"
                          checked={formik.values.offerOtherPlans}
                          onChange={formik.handleChange}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="offerOtherPlans">
                          Diğer paketler bu paketin atandığı kullanıcıya görünsün
                        </label>
                        <div className="text-muted" style={{ fontSize: ".75rem" }}>
                          İşaretlenmezse firma abonelik ekranında başka paket göremez; yalnızca
                          kendisine atadığınız bu paketin detaylarını görür.
                        </div>
                      </div>

                      {formik.values.offerOtherPlans && (
                        <div className="mt-3 border rounded-3 p-3" style={{ background: "#fbfcfe" }}>
                          <Label className="mb-1">Hangi paketleri görebilsin?</Label>
                          <div className="text-muted mb-2" style={{ fontSize: ".72rem" }}>
                            Herkese açık paketler arasından seçin. Hiçbiri seçilmezse firma tüm
                            açık paketleri görür. Firmanın kendi paketi her durumda listede kalır.
                          </div>

                          {publicPlans.length === 0 ? (
                            <div className="text-muted small">Seçilebilecek herkese açık paket yok.</div>
                          ) : (
                            <div style={{ maxHeight: 220, overflowY: "auto", overscrollBehavior: "contain" }}>
                              {publicPlans.map((p) => {
                                const checked = (formik.values.visiblePlanIds || []).includes(p.id)
                                return (
                                  <div
                                    key={p.id}
                                    className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2 mb-2"
                                    style={{ cursor: "pointer", borderColor: checked ? NAVY : undefined, background: checked ? "#f2f6fc" : "#fff" }}
                                    onClick={() => toggleVisiblePlan(p.id)}
                                  >
                                    <div className="form-check mb-0">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`vis-plan-${p.id}`}
                                        checked={checked}
                                        onChange={() => toggleVisiblePlan(p.id)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <label className="form-check-label fw-semibold" htmlFor={`vis-plan-${p.id}`}>
                                        {p.name}
                                      </label>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: ".74rem" }}>
                                      {p.durationLabel || "—"} · {formatTRYAmount(p.basePrice)} ₺
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Section>

                {/* 6 — Durum: eskiden sağdaki ayrı kolonda duruyordu; diğer form
                    ekranlarıyla tutarlı olsun diye tek kolona, sıradaki adım olarak alındı. */}
                <Section step="6" title="Durum" description="Paketin satışa/atamaya açık olup olmadığı ve deneme ayarı.">
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="active"
                      name="active"
                      checked={formik.values.active}
                      onChange={formik.handleChange}
                    />
                    <label className="form-check-label" htmlFor="active">Paket aktif</label>
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>
                      Pasif paket ne satılabilir ne atanabilir.
                    </div>
                  </div>

                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="trialAllowed"
                      name="trialAllowed"
                      checked={formik.values.trialAllowed}
                      onChange={formik.handleChange}
                    />
                    <label className="form-check-label" htmlFor="trialAllowed">
                      14 günlük deneme süresinde denenebilir
                    </label>
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>
                      Firma Paketler ekranında bu paket için &quot;Denemeyi başlat&quot; düğmesini görür.
                      Deneme hakkı firmaya aittir: 14 günlük tek pencerede kaç paket denerse denesin süre uzamaz.
                    </div>
                  </div>

                  {isEdit && (
                    <div className="border-top mt-3 pt-3">
                      {/* Kapsam: paket herkese açık mı, belirli firmalara özel mi */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="text-muted" style={{ fontSize: ".78rem" }}>Kapsam</span>
                        {plan?.visibleToCustomers !== false ? (
                          <span
                            className="badge border-0"
                            style={{ borderRadius: 999, background: "#EBF0F7", color: NAVY, fontWeight: 600 }}
                          >
                            Genel
                          </span>
                        ) : (
                          <span
                            className="badge border-0"
                            style={{ borderRadius: 999, background: "#eef1f5", color: "#6c757d", fontWeight: 600 }}
                          >
                            Firmaya özel
                          </span>
                        )}
                      </div>

                      {plan?.visibleToCustomers === false && (
                        <div className="text-muted mb-2" style={{ fontSize: ".72rem" }}>
                          Herkese açık listede görünmez; yalnızca atandığı firmalarda geçerli.
                        </div>
                      )}

                      {/* Paketin fiilen aktif olduğu firmalar — genel paketlerde de gösterilir */}
                      <div className="text-muted mb-1" style={{ fontSize: ".78rem" }}>
                        {plan?.visibleToCustomers !== false ? "Bu pakette olan firmalar" : "Atanan firmalar"}
                        {assignedCompanies.length > 0 && (
                          <span className="ms-1">({assignedCompanies.length})</span>
                        )}
                      </div>
                      {assignedCompanies.length === 0 ? (
                        <div className="text-muted" style={{ fontSize: ".75rem" }}>
                          Bu paketi kullanan firma yok.
                        </div>
                      ) : (
                        <div className="assigned-list">
                          {assignedCompanies.map((name, i) => (
                            <div key={`${name}-${i}`} className="assigned-row">
                              <i className="bx bx-buildings me-1" style={{ color: NAVY }} />
                              {name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Section>

                <div className="d-flex justify-content-end gap-2">
                  <Button color="light" className="btn-rounded px-4" onClick={() => navigate("/admin/subscription-plans")} disabled={saving}>
                    İptal
                  </Button>
                  <Button className="btn-navy btn-rounded px-4" onClick={formik.handleSubmit} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : isEdit ? "Kaydet" : "Paketi Oluştur"}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </div>
    </div>
  )
}

export default SubscriptionPlanForm
